import { noop } from 'lodash-es';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { Box } from '~/eds/src/components/Box/Box';
import { Checkbox } from '~/eds/src/components/Checkbox/Checkbox';
import { Icon } from '~/eds/src/components/Icon/Icon';
import { IconButton } from '~/eds/src/components/IconButton/IconButton';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Text } from '~/eds/src/components/Text/Text';
import { useToggle } from '~/eds/src/hooks/useToggle';
import { Variation } from '~/features/guidelines/variation/Variation';
import { GuidelineClauseVariation } from '~/redux/api/types/playbooks/guidelines';
import HtmlContent from '~/ui/components/HtmlContent/HtmlContent';
import { copyToClipboard } from '~/utils/helper.utils';

type Props = {
  name: string;
  guideline: string;
  sample: string;
  suggestRedlines: boolean;
  variations?: Array<GuidelineClauseVariation>;
  mode?: 'boxed' | 'inline';

  onDelete?: () => void;
};

export const GuidelineReadOnly = ({
  name,
  guideline,
  sample,
  suggestRedlines,
  mode = 'boxed',
  variations = [],

  onDelete,
}: Props) => {
  const intl = useIntl();
  const handleCopyToClipboard = () => {
    copyToClipboard(
      guideline,
      intl.formatMessage({
        id: 'GuidelineReadOnly.copySuccess',
        defaultMessage: 'Guideline copied to clipboard',
      }),
    );
  };
  const [collapsed, toggleCollapsed] = useToggle(false);

  const processHtml = (html: string) => {
    const textarea = document.createElement('textarea');
    textarea.innerHTML = html;
    const decoded = textarea.value;

    return decoded
      .replace(
        /<pre>\s*/gi,
        '<div style="white-space: normal; word-break: break-word; overflow-wrap: anywhere; max-width: 100%;">',
      )
      .replace(/\s*<\/pre>/gi, '</div>');
  };

  return (
    <Layout
      direction="column"
      border={mode === 'boxed' ? 'border.divider' : undefined}
      borderRadius={4}
    >
      {mode === 'boxed' && (
        <Layout
          onClick={toggleCollapsed}
          py={3}
          px={4}
          align="center"
          justify="space-between"
        >
          <Text
            overflow="hidden"
            textOverflow="ellipsis"
            whiteSpace="nowrap"
            flex={1}
          >
            {name}
          </Text>
          <Icon icon={collapsed ? 'chevron-right' : 'chevron-down'} />
        </Layout>
      )}

      {!collapsed && (
        <Layout direction="column" px={4} pb={4} spacing={4} minWidth={0}>
          <Layout direction="column" minWidth={0}>
            <Text variant="tiny" color="text.quiet">
              <FormattedMessage
                id="GuidelineReadOnly.guidelineLabel"
                defaultMessage="Guideline"
                description="Label for guideline section"
              />
            </Text>
            <Box
              bg="background.info"
              borderRadius={4}
              overflowX="auto"
              maxWidth="100%"
              px={3}
            >
              <HtmlContent
                html={processHtml(guideline)}
                style={{
                  wordBreak: 'break-word',
                  overflowWrap: 'anywhere',
                  maxWidth: '100%',
                  width: '100%',
                  fontSize: '14px',
                  lineHeight: '150%',
                }}
              />
            </Box>
          </Layout>
          <Layout direction="column" minWidth={0}>
            <Text variant="tiny" color="text.quiet">
              <FormattedMessage
                id="GuidelineReadOnly.sampleLabel"
                defaultMessage="Sample Wording"
                description="Label for sample wording section"
              />
            </Text>
            <Box borderLeft="border" overflowX="auto" maxWidth="100%" px={3}>
              <HtmlContent
                html={processHtml(sample)}
                style={{
                  wordBreak: 'break-word',
                  overflowWrap: 'anywhere',
                  maxWidth: '100%',
                  width: '100%',
                  fontSize: '14px',
                  lineHeight: '150%',
                }}
              />
            </Box>
          </Layout>

          <Layout direction="column" spacing={2}>
            {variations.map((variation) => (
              <Variation
                key={variation.clause_variation_id}
                variation={variation}
              />
            ))}
          </Layout>

          <Layout align="center" justify="space-between">
            <Checkbox
              option={{
                label: intl.formatMessage({
                  id: 'GuidelineReadOnly.suggestRedlinesLabel',
                  defaultMessage: 'Suggest Redlines',
                  description: 'Label for suggest redlines checkbox',
                }),
                value: true,
              }}
              value={suggestRedlines}
              name={''}
              onChange={noop}
              disabled
            />
            {mode === 'boxed' && (
              <IconButton
                tooltip={intl.formatMessage(
                  onDelete
                    ? {
                        id: 'GuidelineReadOnly.deleteGuidelineTooltip',
                        defaultMessage: 'Delete Guideline',
                        description: 'Tooltip for delete guideline button',
                      }
                    : {
                        id: 'GuidelineReadOnly.copyToClipboardTooltip',
                        defaultMessage: 'Copy to clipboard',
                        description: 'Tooltip for copy to clipboard button',
                      },
                )}
                icon={onDelete ? 'trash' : 'clipboard'}
                onClick={onDelete ? onDelete : handleCopyToClipboard}
              />
            )}
          </Layout>
        </Layout>
      )}
    </Layout>
  );
};
